import React, { useEffect, useState } from 'react';
import {
    TextField,
    Button,
    IconButton,
    MenuItem,
    Box,
    Modal,
    Divider,
    CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import modalStyle from "../../styles/modalStyle";
import Field from "../../interfaces/field";
import Case from '../../interfaces/case';
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import apiClient from '../../axiosClient';
import { CustomerDetailRequest } from '../../interfaces/customerDetail';
import { v4 } from 'uuid';
import InputConfig from '../../interfaces/inputConfig';
import FIELD_INPUT_CONFIG from '../../constants/fieldInputConfig';

const NoBackdrop = () => null;

interface FieldByUser {
    fieldName: string;
    value: string;
}

interface AddCustomerDetailsModalProps {
    open: boolean;
    onClose: () => void;
    selectedRow: Case;
}

const AddCustomerDetailsModal: React.FC<AddCustomerDetailsModalProps> = ({ open, onClose, selectedRow }) => {
    const [fields, setFields] = useState<FieldByUser[]>([{ fieldName: '', value: '' }]);// fields added by the user
    const [fieldOptions, setFieldOptions] = useState<Field[]>([]);
    const standardFields = useSelector((state: RootState) => state.fields.fields);
    const [customerDetailSelected, setCustomerDetailSelected] = useState<string>('');
    const [customerDetailAttributeOptions, setCustomerDetailAttributeOptions] = useState<Field[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    const loadFields = async () => {
        try {
            setFieldOptions(
                standardFields.filter(
                    (field) => field.field_type === "CUSTOMER_DETAIL_ATTRIBUTE"
                )
            );
        } catch (err) {
            alert('Failed to load field options. ' + String(err));
        }
    };
    const loadCustomerDetailAttributeOptions = async () => {

        try {
            setCustomerDetailAttributeOptions (
                standardFields.filter(
                    (field) => field.field_type === "CUSTOMER_DETAIL"
                )
            )
        } catch (err) {
            alert('Failed to load customer Detail Options. ' + String(err));
        }
    }
    useEffect(() => {
        loadFields();
        if (customerDetailSelected !=="customer") {
            setFields([{ fieldName: 'name', value: '' },{ fieldName: '', value: '' }])
        }else{
            setFields([ { fieldName: '', value: '' }]);
        }
    }, [standardFields, customerDetailSelected]);

    useEffect(() => {
        loadCustomerDetailAttributeOptions();
    },[])

    const addField = () => {
        setFields([...fields, { fieldName: '', value: '' }]);
    };

    const handleFieldChange = (index: number, key: keyof FieldByUser, value: string, fieldConfig?: InputConfig) => {
        const updatedFields = [...fields];
        if (key === 'fieldName') {
            updatedFields[index] = { fieldName: value, value: ''};
        } else if (key === 'value') {
            updatedFields[index].value = fieldConfig?.replaceRegex ? value.replace(fieldConfig?.replaceRegex, '') : value;
        }
        setFields(updatedFields);
    };

    const removeField = (index: number) => {
        const updatedFields = fields.filter((_, i) => i !== index);
        setFields(updatedFields);
    };

    const handleSave = async () => {
        if (!customerDetailSelected || fields.length === 0 || fields.some(field => field.fieldName === '' || field.value === '')) {
            alert('Please select customer reference option, add at least one field, and fill all fields completely');
            return;
        }
        try {
            setLoading(true);
            const randomUuid:string = v4();
            const caseDetailRequest: CustomerDetailRequest[] = fields.map((field) => {
                const fieldOption = fieldOptions.find(option => option.field_name === field.fieldName);
                const customerDetailField = customerDetailAttributeOptions.find(option => option.field_name===customerDetailSelected);
                const fieldInputConfigObject = FIELD_INPUT_CONFIG[fieldOption?.field_value_type ?? ''] ?? FIELD_INPUT_CONFIG.default;
                if (!fieldOption) {
                    throw new Error(`Field ID not found for field name: ${field.fieldName}`);
                }
                if (!customerDetailField) {
                    throw new Error(`Field ID not found for field_name: ${customerDetailSelected}`);
                }
                if (fieldInputConfigObject?.testRegex && !fieldInputConfigObject?.testRegex?.test(field.value)) {
                    throw new Error(`Field Type: ${fieldOption.field_value_type}\nValue: ${field.value}\n\n` + (fieldInputConfigObject?.warningMsg));
                }
                return {
                    customer_id: selectedRow.customer_id,
                    client_name: selectedRow.client_name,
                    group_id: customerDetailSelected ==="customer" ? "customer" : randomUuid,
                    field_id: fieldOption.field_id,
                    field_value: field.value,
                    relation: "",
                    tag: "",
                    score: 100,
                    customer_detail_field_id: customerDetailField.field_id,
                    client_reference_id:customerDetailSelected ==="customer" ? undefined : selectedRow.client_reference_id,
                    
                };
            });
            try {
                const response = await apiClient.post('/api/v1/customer_detail', caseDetailRequest);
                if (response.status === 200) {
                    alert('Case Detail added successfully');
                    setFields([{fieldName:'',value:''}])
                    setLoading(false);
                    onClose();
                }
            } catch (error: any) {
                if (error.response) {
                    alert('Error response data:' + error.response.data.message);
                } else if (error.request) {
                    alert('No response received:' + error.request);
                } else {
                    alert('Error message:' + error.message);
                }
            }
        } catch (error) {
            alert(error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose} sx={modalStyle} disableEnforceFocus
            BackdropComponent={NoBackdrop}
        >
            <Box >
                <IconButton
                    onClick={onClose}
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                >
                    <CloseIcon color='error' fontSize='large' />
                </IconButton>
                <Divider sx={{ mt: 4 }}></Divider>
                <Box flex={1} flexDirection={"row"} >
                    <Box>
                        <TextField
                            select
                            label="Select Customer Reference Option"
                            value={customerDetailSelected}
                            onChange={(e) => setCustomerDetailSelected(e.target.value)}
                            variant="outlined"
                            fullWidth
                            margin="normal"
                        >
                            <MenuItem value="">
                                <em>Select Field Type</em>
                            </MenuItem>
                            {customerDetailAttributeOptions.map((option) => (
                                <MenuItem key={option.field_id} value={option.field_name}>
                                    {option.field_description}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    {fields.map((field, index) => {
                        const option = fieldOptions.find(o => o.field_name === field.fieldName);
                        const fieldInputConfigObject = FIELD_INPUT_CONFIG[option?.field_value_type ?? ''] ?? FIELD_INPUT_CONFIG.default;
                        return (
                            <Box key={index} display="flex" alignItems="center">
                                <TextField
                                    select
                                    label="Field Type"
                                    value={field.fieldName}
                                    onChange={(e) => handleFieldChange(index, 'fieldName', e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    margin="normal"
                                >
                                    <MenuItem value="">
                                        <em>Select Field Type</em>
                                    </MenuItem>
                                    {fieldOptions.map((option) => (
                                        <MenuItem key={option.field_id} value={option.field_name}>
                                            {option.field_description}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <IconButton
                                    disabled={fields.length === 1}
                                    onClick={() => removeField(index)}
                                    color={fields.length === 1 ? "inherit" : "error"}
                                >
                                    <CloseIcon />
                                </IconButton>
                                <TextField
                                    label="Field"
                                    value={field.value}
                                    onChange={(e) => handleFieldChange(
                                        index, "value", e.target.value, fieldInputConfigObject
                                    )}
                                    variant="outlined"
                                    fullWidth
                                    margin="normal"
                                    type={fieldInputConfigObject?.inputType}
                                    slotProps={{
                                        htmlInput: {
                                            maxLength: fieldInputConfigObject?.maxLen
                                        }
                                    }}
                                />
                            </Box>
                        );
                    })}
                    <Button
                        onClick={addField}
                        variant="outlined"
                        fullWidth
                        sx={{ mb: 2 }}
                    >
                        + Add More Fields
                    </Button>
                    <Button
                        onClick={handleSave}
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled= { !customerDetailSelected || fields.length === 0 || fields.some(field => field.fieldName === '' || field.value === '')}
                    >
                    {loading ? <CircularProgress size={24} color="inherit" /> : 'SAVE DETAILS'}
                    </Button>
                </Box>
            </Box>

        </Modal>
    );
};

export default AddCustomerDetailsModal;
